import React from 'react';
import PropTypes from 'prop-types';

/**
 * A highly reusable, accessible semantic button.
 * Renders as a `<button>` by default, but if rendered as a `<div>` (using `as="div"`),
 * it seamlessly attaches W3C ARIA properties (like role="button", tabIndex, and onKeyDown)
 * to avoid SonarQube "non-interactive element" errors.
 */
const CustomButton = ({ as: Component = 'button', onClick, onKeyDown, children, ...rest }) => {
   const isNativeInteractive = Component === 'button' || Component === 'a';

   const handleKeyDown =
      !isNativeInteractive && onClick
         ? e => {
              if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                 e.preventDefault();
                 onClick(e);
              }
              if (onKeyDown) onKeyDown(e);
           }
         : onKeyDown;

   return (
      <Component
         onClick={onClick}
         onKeyDown={handleKeyDown}
         type={Component === 'button' ? rest.type || 'button' : undefined}
         role={!isNativeInteractive && onClick ? 'button' : rest.role}
         tabIndex={!isNativeInteractive && onClick ? 0 : rest.tabIndex}
         {...rest}
      >
         {children}
      </Component>
   );
};

CustomButton.propTypes = {
   as: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
   onClick: PropTypes.func,
   onKeyDown: PropTypes.func,
   children: PropTypes.node,
};

export default CustomButton;
